(() => {
  const SELECTOR = '[data-calinfi-odun-features]';
  const controllers = new WeakMap();

  const initSection = (section) => {
    if (!section || section.dataset.odunFeaturesInitialized === 'true') return;

    const track = section.querySelector('[data-odun-features-track]');
    const slides = Array.from(section.querySelectorAll('[data-odun-feature-slide]'));
    const dots = Array.from(section.querySelectorAll('[data-odun-features-dot]'));
    const prev = section.querySelector('[data-odun-features-prev]');
    const next = section.querySelector('[data-odun-features-next]');

    if (!track || slides.length === 0) return;

    section.dataset.odunFeaturesInitialized = 'true';

    const controller = new AbortController();
    const { signal } = controller;
    let activeIndex = 0;
    let currentPosition = slides.length > 1 ? 1 : 0;
    controllers.set(section, controller);

    const stripEditorAttributes = (node) => {
      [node, ...node.querySelectorAll('*')].forEach((element) => {
        Array.from(element.attributes).forEach((attribute) => {
          if (attribute.name.startsWith('data-shopify')) {
            element.removeAttribute(attribute.name);
          }
        });

        element.removeAttribute('id');
      });
    };

    const createClone = (source, cloneIndex, positionName) => {
      const clone = source.cloneNode(true);

      stripEditorAttributes(clone);
      clone.classList.remove('is-active', 'is-next');
      clone.classList.add('calinfi-odun-features__slide--clone', `calinfi-odun-features__slide--clone-${positionName}`);
      clone.removeAttribute('data-odun-feature-slide');
      clone.dataset.odunFeatureClone = String(cloneIndex);
      clone.setAttribute('aria-hidden', 'true');

      return clone;
    };

    let renderedSlides = slides;

    if (slides.length > 1) {
      const beforeClone = createClone(slides[slides.length - 1], slides.length - 1, 'before');
      const afterClone = createClone(slides[0], 0, 'after');

      track.insertBefore(beforeClone, slides[0]);
      track.appendChild(afterClone);
      renderedSlides = [beforeClone, ...slides, afterClone];
    }

    const getTargetLeft = () => {
      if (window.matchMedia('(max-width: 989px)').matches) return 0;

      const rawValue = window.getComputedStyle(section).getPropertyValue('--slider-left').trim();

      if (rawValue.endsWith('%')) {
        return section.getBoundingClientRect().width * (parseFloat(rawValue) / 100);
      }

      return parseFloat(rawValue) || 0;
    };

    const getStep = () => {
      const firstSlide = renderedSlides[0];
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || 0);

      return firstSlide.getBoundingClientRect().width + gap;
    };

    const getStablePosition = () => (slides.length > 1 ? activeIndex + 1 : 0);

    const updateState = () => {
      section.dataset.odunActiveIndex = String(activeIndex);

      renderedSlides.forEach((slide, renderedIndex) => {
        const cloneIndex = slide.dataset.odunFeatureClone;
        const originalIndex = cloneIndex === undefined ? slides.indexOf(slide) : Number(cloneIndex);
        const isActive = renderedIndex === currentPosition;
        const isPreview = Math.abs(renderedIndex - currentPosition) === 1;

        slide.classList.toggle('is-active', isActive);
        slide.classList.toggle('is-next', renderedIndex === currentPosition + 1);
        slide.classList.toggle('is-preview', isPreview);
        slide.setAttribute('aria-hidden', isActive || isPreview ? 'false' : 'true');

        if (cloneIndex === undefined) {
          slide.setAttribute('aria-hidden', originalIndex === activeIndex ? 'false' : 'true');
        }
      });

      dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === activeIndex;

        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    };

    const moveToPosition = (position, shouldAnimate = true) => {
      currentPosition = position;

      if (!shouldAnimate) {
        track.style.transition = 'none';
      }

      const offset = getTargetLeft() - (getStep() * currentPosition);

      track.style.transform = `translate3d(${offset}px, 0, 0)`;
      updateState();

      if (!shouldAnimate) {
        window.requestAnimationFrame(() => {
          track.style.transition = '';
        });
      }
    };

    const setActive = (index) => {
      const previousIndex = activeIndex;
      const nextIndex = (index + slides.length) % slides.length;
      let nextPosition = slides.length > 1 ? nextIndex + 1 : 0;

      if (slides.length > 1 && previousIndex === slides.length - 1 && nextIndex === 0 && index > previousIndex) {
        nextPosition = slides.length + 1;
      }

      if (slides.length > 1 && previousIndex === 0 && nextIndex === slides.length - 1 && index < previousIndex) {
        nextPosition = 0;
      }

      activeIndex = nextIndex;
      moveToPosition(nextPosition);
    };

    prev?.addEventListener('click', () => {
      setActive(activeIndex - 1);
    }, { signal });

    next?.addEventListener('click', () => {
      setActive(activeIndex + 1);
    }, { signal });

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = Number(dot.dataset.odunFeaturesIndex);

        if (Number.isFinite(index)) setActive(index);
      }, { signal });
    });

    track.addEventListener('transitionend', (event) => {
      if (event.target !== track || event.propertyName !== 'transform') return;

      if (currentPosition === 0 || currentPosition === slides.length + 1) {
        moveToPosition(getStablePosition(), false);
      }
    }, { signal });

    window.addEventListener('resize', () => {
      moveToPosition(getStablePosition(), false);
    }, { signal });

    moveToPosition(getStablePosition(), false);
  };

  document.querySelectorAll(SELECTOR).forEach(initSection);

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.matches?.(SELECTOR) ? event.target : event.target.querySelector?.(SELECTOR);

    initSection(section);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.(SELECTOR) ? event.target : event.target.querySelector?.(SELECTOR);
    const controller = controllers.get(section);

    controller?.abort();
    controllers.delete(section);

    if (section) delete section.dataset.odunFeaturesInitialized;
  });
})();
